import axios from 'axios';
import { z } from 'zod';
import type { LegacySkuFoundResponse } from '../types/apiTypes';
import { validateResponse } from './validateResponse';
import { ProductVariantWeightSchema } from '../schemas/apiResponse.schemas';

const SkuListResponseSchema = z.object({ skus: z.array(z.string()).optional() });
const ShelfNamesResponseSchema = z.object({ shelfNames: z.array(z.string()).optional() });
const SkuNameItemSchema = z.object({
  sku: z.string(),
  name: z.string(),
});
const SkuNameListResponseSchema = z.object({
  items: z.array(SkuNameItemSchema).optional(),
});

export interface SkuNameItem {
  sku: string;
  name: string;
}

export interface ProductVariantWeight {
  sku: string;
  name: string;
  weightOz: number | null;
}

function variantWeightPath(sku: string): string {
  return `/api/sku-list/${encodeURIComponent(sku)}/weight`;
}

export const findSKU = async (sku: string): Promise<LegacySkuFoundResponse | null> => {
  try {
    const response = await axios.post('/api/sku-list', { sku });
    return response.data;
  } catch (error: unknown) {
    console.error(`Error finding SKU ${sku}:`, error);
    return null;
  }
};

export const getAllSKUs = async (): Promise<string[]> => {
  try {
    const response = await axios.get('/api/sku-list/all');
    return validateResponse(SkuListResponseSchema, response.data, 'GET /api/sku-list/all').skus || [];
  } catch (error: unknown) {
    console.error('Error fetching all SKUs:', error);
    return [];
  }
};

/** Parent bundle SKUs only (distinct `bundle_variant_sku` from PIM bundle_components). */
export const getBundleParentSkus = async (): Promise<string[]> => {
  try {
    const response = await axios.get('/api/sku-list/bundle-parents');
    return (
      validateResponse(SkuListResponseSchema, response.data, 'GET /api/sku-list/bundle-parents')
        .skus || []
    );
  } catch (error: unknown) {
    console.error('Error fetching bundle parent SKUs:', error);
    return [];
  }
};

export const getAllSkusWithNames = async (): Promise<SkuNameItem[]> => {
  try {
    const response = await axios.get('/api/sku-list/all-with-names');
    return (
      validateResponse(SkuNameListResponseSchema, response.data, 'GET /api/sku-list/all-with-names')
        .items || []
    );
  } catch (error: unknown) {
    console.error('Error fetching SKU name list:', error);
    return [];
  }
};

export const searchProductsByName = async (
  query: string,
  limit?: number,
  matchSku = false,
): Promise<SkuNameItem[]> => {
  try {
    const params = new URLSearchParams({ q: query });
    if (limit != null) {
      params.set('limit', String(limit));
    }
    if (matchSku) {
      params.set('matchSku', '1');
    }
    const response = await axios.get(`/api/sku-list/search-by-name?${params.toString()}`);
    return (
      validateResponse(SkuNameListResponseSchema, response.data, 'GET /api/sku-list/search-by-name')
        .items || []
    );
  } catch (error: unknown) {
    console.error('Error searching products by name:', error);
    return [];
  }
};

export const getAllShelfNames = async (): Promise<string[]> => {
  try {
    const response = await axios.get('/api/shelves/all');
    return validateResponse(ShelfNamesResponseSchema, response.data, 'GET /api/shelves/all').shelfNames || [];
  } catch (error: unknown) {
    console.error('Error fetching all shelf names:', error);
    return [];
  }
};

export const getVariantWeight = async (sku: string): Promise<ProductVariantWeight | null> => {
  try {
    const response = await axios.get(variantWeightPath(sku));
    return validateResponse(ProductVariantWeightSchema, response.data, 'GET /api/sku-list/:sku/weight');
  } catch (error: unknown) {
    console.error(`Error fetching weight for ${sku}:`, error);
    return null;
  }
};

export const updateVariantWeight = async (
  sku: string,
  weightOz: number,
): Promise<ProductVariantWeight | null> => {
  try {
    const response = await axios.patch(variantWeightPath(sku), { weightOz });
    return validateResponse(ProductVariantWeightSchema, response.data, 'PATCH /api/sku-list/:sku/weight');
  } catch (error: unknown) {
    console.error(`Error updating weight for ${sku}:`, error);
    return null;
  }
};
